import { useTranslation } from '../../../shared/i18n';
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, Table, Tabs, Button, Space, Typography, message } from 'antd';
import { PlusOutlined, ReloadOutlined } from '@ant-design/icons';
import PageHeader from '../../../shared/components/PageHeader';
import supportService from '../../services/supportService';
import { formatDateTime } from '../../../shared/utils/date';
import { userPath } from '../../../shared/config/paths';
import { rowLink, ROW_LINK_CLASS } from '../../../shared/utils/rowLink';
import { TicketStatusTag, serviceText, ok } from './common';

const { Text } = Typography;

const Tickets = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const [status, setStatus] = useState('');
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(20);
    const [items, setItems] = useState([]);
    const [total, setTotal] = useState(0);
    const [loading, setLoading] = useState(false);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const r = await supportService.getTickets({ status: status || undefined, page, per_page: pageSize });
            if (ok(r)) {
                setItems(Array.isArray(r.data?.items) ? r.data.items : []);
                setTotal(r.data?.total || 0);
            } else {
                message.error(r?.msg || t('Failed to load'));
            }
        } catch (e) {
            message.error(e?.msg || t('Failed to load'));
        } finally {
            setLoading(false);
        }
    }, [status, page, pageSize, t]);

    useEffect(() => { load(); }, [load]);

    const columns = [
        {
            title: t('Tickets'),
            dataIndex: 'subject',
            render: (subject, r) => (
                <Space direction="vertical" size={0} style={{ minWidth: 0 }}>
                    <Text strong ellipsis style={{ maxWidth: 420 }}>{subject}</Text>
                    <Text type="secondary" style={{ fontSize: 12 }}>
                        #{r.number || r.id}
                        <span className="support-mobile-only"> · {formatDateTime(r.last_reply_at || r.created_at)}</span>
                    </Text>
                </Space>
            ),
        },
        {
            title: t('Related service'),
            key: 'service',
            responsive: ['md'],
            render: (_, r) => serviceText(t, r.service_type, r.resource_label) || <Text type="secondary">-</Text>,
        },
        {
            title: t('Department'),
            dataIndex: 'department_name',
            responsive: ['lg'],
            render: (v) => (v ? t(v) : '-'),
        },
        {
            title: t('Status'),
            dataIndex: 'status',
            width: 96,
            render: (v) => <TicketStatusTag status={v} />,
        },
        {
            title: t('Last updated'),
            key: 'updated',
            responsive: ['sm'],
            width: 170,
            render: (_, r) => (
                <Space direction="vertical" size={0}>
                    <span>{formatDateTime(r.last_reply_at || r.created_at)}</span>
                    {r.last_reply_by && (
                        <Text type="secondary" style={{ fontSize: 12 }}>
                            {r.last_reply_by === 'admin' ? t('Support') : t('Me')}
                        </Text>
                    )}
                </Space>
            ),
        },
    ];

    const tabs = [
        { key: '', label: t('All') },
        { key: 'open', label: t('Processing') },
        { key: 'answered', label: t('Answered') },
        { key: 'closed', label: t('Closed') },
    ];

    return (
        <div>
            <PageHeader
                title={t('My tickets')}
                extra={(
                    <>
                        <Button icon={<ReloadOutlined />} onClick={load} loading={loading}>{t('Refresh')}</Button>
                        <Button type="primary" icon={<PlusOutlined />} onClick={() => navigate(userPath('/support/tickets/new'))}>
                            {t('New ticket')}
                        </Button>
                    </>
                )}
            />
            <Card bordered={false} styles={{ body: { paddingTop: 4 } }}>
                <Tabs
                    activeKey={status}
                    items={tabs}
                    onChange={(k) => { setPage(1); setStatus(k); }}
                />
                <style>{'.support-mobile-only{display:none}@media (max-width:575px){.support-mobile-only{display:inline}}'}</style>
                <Table
                    rowKey="id"
                    columns={columns}
                    dataSource={items}
                    loading={loading}
                    onRow={rowLink(navigate, (r) => `/support/tickets/${r.id}`)}
                    rowClassName={() => ROW_LINK_CLASS}
                    locale={{ emptyText: t('No tickets') }}
                    pagination={{
                        current: page,
                        pageSize,
                        total,
                        showSizeChanger: true,
                        size: 'small',
                        onChange: (p, s) => { setPage(s !== pageSize ? 1 : p); setPageSize(s); },
                    }}
                />
            </Card>
        </div>
    );
};

export default Tickets;
